/**
 * Program Helper Utilities
 * Extracted from ClubDetails.js to support the new Card Grid layout
 */

import { 
  FaFutbol, 
  FaBasketballBall, 
  FaTableTennis,
  FaVolleyballBall,
  FaBaseballBall,
  FaHockeyPuck,
  FaRunning,
  FaSwimmer
} from 'react-icons/fa';

/**
 * Sport icon mapping
 */
export const sportIcons = {
  Soccer: FaFutbol,
  Basketball: FaBasketballBall,
  Tennis: FaTableTennis,
  Volleyball: FaVolleyballBall,
  Baseball: FaBaseballBall,
  Hockey: FaHockeyPuck,
  'Track & Field': FaRunning,
  Swimming: FaSwimmer,
  // Default fallback
  default: FaFutbol
};

/**
 * Get the appropriate icon component for a sport
 * @param {string} sport - Sport name
 * @returns {React.Component} Icon component
 */
export const getSportIcon = (sport) => {
  return sportIcons[sport] || sportIcons.default;
};

/**
 * Check if a program is free (no cost)
 * @param {Object} program - Program object
 * @returns {boolean}
 */
export const isProgramFree = (program) => {
  const amount = program?.cost?.feeStructure?.[0]?.amount;
  return !amount || Number(amount) === 0;
};

/**
 * Check if registration is closed based on end dates
 * @param {Object} program - Program object
 * @returns {boolean}
 */
export const isRegistrationClosed = (program) => {
  const fees = program?.cost?.registrationFees;
  if (!fees || fees.length === 0) return false;
  
  // Get all valid end dates
  const endDates = fees
    .map(fee => fee.endDate)
    .filter(date => !!date)
    .map(date => new Date(date));
  
  if (endDates.length === 0) return false;
  
  // Find the max end date
  const maxEndDate = new Date(Math.max(...endDates));
  return new Date() > maxEndDate;
};

/**
 * Get registration dates info from program's registration fees
 * @param {Object} program - Program object
 * @returns {Object} Registration dates info
 */
export const getRegistrationDates = (program) => {
  const fees = program?.cost?.registrationFees;
  
  // Default response when no registration fees exist
  if (!fees || fees.length === 0) {
    return {
      hasRegistrationDates: false,
      minStartDate: null,
      maxEndDate: null,
      isOpen: false,
      isClosed: false,
      isNotStarted: false,
      daysUntilClose: null,
      daysUntilOpen: null,
      statusMessage: null,
      statusType: 'none' // 'none' | 'upcoming' | 'open' | 'closed'
    };
  }
  
  // Get all valid start dates
  const startDates = fees
    .map(fee => fee.startDate)
    .filter(date => !!date)
    .map(date => new Date(date));
  
  // Get all valid end dates
  const endDates = fees
    .map(fee => fee.endDate)
    .filter(date => !!date)
    .map(date => new Date(date));
  
  // No valid dates
  if (startDates.length === 0 && endDates.length === 0) {
    return {
      hasRegistrationDates: false,
      minStartDate: null,
      maxEndDate: null,
      isOpen: true, // No dates means always open
      isClosed: false,
      isNotStarted: false,
      daysUntilClose: null,
      daysUntilOpen: null,
      statusMessage: 'Open registration',
      statusType: 'open'
    };
  }
  
  const now = new Date();
  now.setHours(0, 0, 0, 0); // Normalize to start of day
  
  // Find min start date (earliest registration opens)
  const minStartDate = startDates.length > 0 
    ? new Date(Math.min(...startDates)) 
    : null;
  
  // Find max end date (latest registration closes)
  const maxEndDate = endDates.length > 0 
    ? new Date(Math.max(...endDates)) 
    : null;
  
  // Calculate status
  let isOpen = true;
  let isClosed = false;
  let isNotStarted = false;
  let daysUntilClose = null;
  let daysUntilOpen = null;
  let statusMessage = '';
  let statusType = 'open';
  
  // Check if registration hasn't started yet
  if (minStartDate && now < minStartDate) {
    isNotStarted = true;
    isOpen = false;
    const timeDiff = minStartDate.getTime() - now.getTime();
    daysUntilOpen = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
    statusMessage = daysUntilOpen === 1 
      ? 'Opens tomorrow' 
      : `Opens in ${daysUntilOpen} days`;
    statusType = 'upcoming';
  }
  // Check if registration is closed
  else if (maxEndDate && now > maxEndDate) {
    isClosed = true;
    isOpen = false;
    statusMessage = 'Registration closed';
    statusType = 'closed';
  }
  // Registration is open
  else if (maxEndDate) {
    const timeDiff = maxEndDate.getTime() - now.getTime();
    daysUntilClose = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
    
    if (daysUntilClose === 0) {
      statusMessage = 'Closes today!';
    } else if (daysUntilClose === 1) {
      statusMessage = 'Closes tomorrow';
    } else if (daysUntilClose <= 7) {
      statusMessage = `Closing in ${daysUntilClose} days`;
    } else {
      statusMessage = `Closing in ${daysUntilClose} days`;
    }
    statusType = 'open';
  } else {
    statusMessage = 'Registration open';
    statusType = 'open';
  }
  
  return {
    hasRegistrationDates: true,
    minStartDate,
    maxEndDate,
    isOpen,
    isClosed,
    isNotStarted,
    daysUntilClose,
    daysUntilOpen,
    statusMessage,
    statusType
  };
};

/**
 * Get program count by category name
 * @param {Array} programs - Array of programs
 * @param {string} categoryName - Category name to filter
 * @returns {number}
 */
export const getProgramCountByCategoryName = (programs, categoryName) => {
  if (!Array.isArray(programs)) return 0;
  
  return programs.filter(p => {
    const programCategory = p.category;
    if (typeof programCategory === 'string') {
      return programCategory === categoryName;
    } else if (programCategory && programCategory.name) {
      return programCategory.name === categoryName;
    }
    return false;
  }).length;
};

/**
 * Get program counts grouped by category
 * @param {Array} programs - Array of programs
 * @param {Array} categories - Array of categories
 * @returns {Object} Counts keyed by category ID
 */
export const getProgramCountsByCategory = (programs, categories) => {
  if (!Array.isArray(programs) || !Array.isArray(categories)) {
    return {};
  }

  const counts = {
    all: programs.length
  };

  categories.forEach(category => {
    if (category._id === 'all') {
      counts[category._id] = programs.length;
    } else {
      counts[category._id] = programs.filter(
        p => p.category?._id === category._id || p.category === category._id
      ).length;
    }
  });

  return counts;
};

/**
 * Get program distribution statistics
 * @param {Array} programs - Array of programs
 * @returns {Object} Distribution stats
 */
export const getProgramDistribution = (programs) => {
  if (!Array.isArray(programs) || programs.length === 0) {
    return {
      mostPopularSport: 'N/A',
      genderDistribution: '0 Co-ed, 0 Male, 0 Female',
      averageCost: 'N/A',
      programsWithStaff: '0 / 0'
    };
  }

  // Most popular sport
  const sportCounts = {};
  programs.forEach(p => {
    sportCounts[p.sport] = (sportCounts[p.sport] || 0) + 1;
  });
  const mostPopular = Object.entries(sportCounts).sort(([, a], [, b]) => b - a)[0];
  const mostPopularSport = mostPopular ? mostPopular[0] : 'N/A';

  // Gender distribution
  const genderCounts = {};
  programs.forEach(p => {
    genderCounts[p.gender] = (genderCounts[p.gender] || 0) + 1;
  });
  const coed = genderCounts['Co-ed'] || 0;
  const male = genderCounts['Male'] || 0;
  const female = genderCounts['Female'] || 0;
  const genderDistribution = `${coed} Co-ed, ${male} Male, ${female} Female`;

  // Average cost
  const costs = programs
    .map(p => p.cost?.feeStructure?.[0]?.amount)
    .filter(cost => cost && !isNaN(cost))
    .map(cost => parseFloat(cost));
  const averageCost = costs.length > 0 
    ? `$${(costs.reduce((sum, cost) => sum + cost, 0) / costs.length).toFixed(0)}`
    : 'N/A';

  // Programs with staff
  const programsWithStaff = programs.filter(p => p.staff && p.staff.length > 0).length;

  return {
    mostPopularSport,
    genderDistribution,
    averageCost,
    programsWithStaff: `${programsWithStaff} / ${programs.length}`
  };
};

/**
 * Format price display
 * @param {Object} program - Program object
 * @returns {string} Formatted price string
 */
export const formatProgramPrice = (program) => {
  const amount = program?.cost?.feeStructure?.[0]?.amount;
  if (!amount || Number(amount) === 0) return 'Free';
  return `$${amount}`;
};

/**
 * Get capacity status for a program
 * @param {Object} program - Program object
 * @param {number} enrolledCount - Current enrolled count
 * @returns {Object} Capacity status info
 */
export const getCapacityStatus = (program, enrolledCount = 0) => {
  const maxParticipants = program?.capacity?.maxParticipants || 0;
  const minParticipants = program?.capacity?.minParticipants || 0;
  const waitlistEnabled = program?.capacity?.waitlistEnabled || false;
  
  if (!maxParticipants) {
    return {
      status: 'unlimited',
      label: 'Unlimited',
      spotsLeft: '∞',
      percentage: 0,
      isFull: false,
      isApproaching: false,
      isBelowMinimum: minParticipants > 0 && enrolledCount < minParticipants,
      waitlistEnabled
    };
  }

  const percentage = Math.round((enrolledCount / maxParticipants) * 100);
  const spotsLeft = Math.max(0, maxParticipants - enrolledCount);
  const isFull = enrolledCount >= maxParticipants;
  const isApproaching = !isFull && percentage >= 80;
  const isBelowMinimum = minParticipants > 0 && enrolledCount < minParticipants;

  let status = 'available';
  let label = 'Available';

  if (isFull) {
    status = 'full';
    label = 'Program Full';
  } else if (isApproaching) {
    status = 'approaching';
    label = 'Almost Full';
  } else if (isBelowMinimum) {
    status = 'belowMinimum';
    label = 'Needs Participants';
  }

  return {
    status,
    label,
    spotsLeft,
    percentage: Math.min(percentage, 100),
    isFull,
    isApproaching,
    isBelowMinimum,
    waitlistEnabled,
    enrolled: enrolledCount,
    max: maxParticipants,
    min: minParticipants
  };
};

/**
 * Filter programs based on search criteria
 * @param {Array} programs - Array of programs
 * @param {Object} filters - Filter criteria
 * @returns {Array} Filtered programs
 */
export const filterPrograms = (programs, filters) => {
  if (!Array.isArray(programs)) return [];
  
  const { query = '', age = '', sport = '', gender = '', category = 'all', priceRange, availability } = filters;

  return programs.filter(program => {
    // Text search
    const matchesQuery = !query || 
      program.name?.toLowerCase().includes(query.toLowerCase()) ||
      program.programDetails?.toLowerCase().includes(query.toLowerCase());
    
    // Age filter
    const matchesAge = !age || 
      (program.age?.min <= parseInt(age) && program.age?.max >= parseInt(age));
    
    // Sport filter
    const matchesSport = !sport || program.sport === sport;
    
    // Gender filter
    const matchesGender = !gender || program.gender === gender;
    
    // Category filter
    const matchesCategory = category === 'all' || 
      program.category?._id === category || 
      program.category === category;

    // Price range filter
    let matchesPrice = true;
    if (priceRange) {
      const amount = program.cost?.feeStructure?.[0]?.amount || 0;
      if (priceRange.min !== undefined) {
        matchesPrice = matchesPrice && amount >= priceRange.min;
      }
      if (priceRange.max !== undefined) {
        matchesPrice = matchesPrice && amount <= priceRange.max;
      }
    }

    // Availability filter
    let matchesAvailability = true;
    if (availability && availability !== 'all') {
      const capacityStatus = getCapacityStatus(
        program, 
        program.capacity?.currentEnrolled || 0
      );
      
      switch (availability) {
        case 'available':
          matchesAvailability = !capacityStatus.isFull;
          break;
        case 'full':
          matchesAvailability = capacityStatus.isFull;
          break;
        case 'waitlist':
          matchesAvailability = capacityStatus.isFull && capacityStatus.waitlistEnabled;
          break;
        default:
          matchesAvailability = true;
      }
    }

    return matchesQuery && matchesAge && matchesSport && matchesGender && 
           matchesCategory && matchesPrice && matchesAvailability;
  });
};

const helpers = {
  sportIcons,
  getSportIcon,
  isProgramFree,
  isRegistrationClosed,
  getRegistrationDates,
  getProgramCountByCategoryName,
  getProgramCountsByCategory,
  getProgramDistribution,
  formatProgramPrice,
  getCapacityStatus,
  filterPrograms
};

export default helpers;










